import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  Users,
  Package,
  MapPin,
  AlertTriangle,
  Activity,
  Boxes,
  Building2,
  CheckCircle2,
  XCircle,
  Clock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { userService } from '../services/userService';
import { inventoryService } from '../services/inventoryService';
import locationService from '../services/locationService';
import { formatDateTime } from '../utils/dateUtils';
import { usePageTitle } from '../contexts/PageTitleContext';
interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  subtitle?: string;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon: Icon, color, subtitle, onClick }) => (
  <div 
    className={`card ${onClick ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}`}
    onClick={onClick}
  >
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <div className="text-sm font-medium text-secondary-500 dark:text-secondary-400 mb-1">{title}</div>
        <div className="text-3xl font-bold text-secondary-900 dark:text-white">{value}</div>
        {subtitle && (
          <div className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">{subtitle}</div>
        )}
      </div>
      <div className={`w-14 h-14 ${color} rounded-lg flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-7 h-7 text-white" />
      </div>
    </div>
  </div>
);

interface QuickActionProps {
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
  onClick: () => void;
}

const QuickAction: React.FC<QuickActionProps> = ({ title, description, icon: Icon, color, onClick }) => (
  <button
    onClick={onClick}
    className="card hover:shadow-lg transition-shadow text-left w-full"
  >
    <div className="flex items-start space-x-4">
      <div className={`w-12 h-12 ${color} rounded-lg flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div className="flex-1">
        <h3 className="text-base font-semibold text-secondary-900 dark:text-white">{title}</h3>
        <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">{description}</p>
      </div>
    </div>
  </button>
);

interface SystemStatusItemProps {
  label: string;
  value: number;
  total: number;
  status: 'success' | 'warning' | 'error';
}

const SystemStatusItem: React.FC<SystemStatusItemProps> = ({ label, value, total, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'success': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'error': return 'bg-red-500';
      default: return 'bg-secondary-500';
    }
  };

  const percentage = total > 0 ? ((value / total) * 100).toFixed(2) : 0;

  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center space-x-3">
        <div className={`w-2 h-2 rounded-full ${getStatusColor()}`}></div>
        <span className="text-sm text-secondary-700 dark:text-secondary-300">{label}</span>
      </div>
      <div className="flex items-center space-x-2">
        <span className="text-sm font-semibold text-secondary-900 dark:text-white">{value}</span>
        <span className="text-xs text-secondary-500 dark:text-secondary-400">/ {total}</span>
        <span className="text-xs text-secondary-500 dark:text-secondary-400">({percentage}%)</span>
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  usePageTitle('Admin Dashboard');
  const navigate = useNavigate();

  // Fetch real data from APIs
  const { data: userStats, isLoading: loadingUsers } = useQuery(
    'userStats',
    () => userService.getUserStats(),
    { 
      refetchInterval: 60000,
      keepPreviousData: true,
      staleTime: 30000,
    }
  );

  const { data: locationStats, isLoading: loadingLocations } = useQuery(
    'locationStats',
    () => locationService.getLocationStats(),
    { 
      refetchInterval: 60000,
      keepPreviousData: true,
      staleTime: 30000,
    }
  );

  const { data: stations, isLoading: loadingStations } = useQuery(
    'stations',
    () => inventoryService.getStations(),
    { 
      refetchInterval: 30000,
      keepPreviousData: true,
      staleTime: 20000,
    }
  );

  const { data: products, isLoading: loadingProducts } = useQuery(
    'products',
    () => inventoryService.getProducts({ limit: 10 }),
    { 
      refetchInterval: 60000,
      keepPreviousData: true,
      staleTime: 40000,
    }
  );

  const { data: capacity, isLoading: loadingCapacity } = useQuery(
    'capacity',
    () => inventoryService.getCapacity(),
    { 
      refetchInterval: 60000,
      keepPreviousData: true,
      staleTime: 40000,
    }
  );

  const { data: recentActivity, isLoading: loadingActivity } = useQuery(
    'recentActivity',
    () => userService.getRecentActivity(10),
    { 
      refetchInterval: 30000,
      keepPreviousData: true,
      staleTime: 20000,
    }
  );

  // Calculate user statistics from roleDistribution
  const calculatedUserStats = useMemo(() => {
    if (!userStats || !userStats.roleDistribution) {
      return { total: 0, active: 0, inactive: 0 };
    }
    
    let totalUsers = 0;
    let activeUsers = 0;
    let inactiveUsers = 0;
    
    userStats.roleDistribution.forEach(role => {
      const total = Number(role.total) || 0;
      const active = Number(role.active) || 0;
      const disabled = Number(role.disabled) || 0;
      
      totalUsers += total;
      activeUsers += active;
      inactiveUsers += disabled;
    });
    
    return {
      total: totalUsers,
      active: activeUsers,
      inactive: inactiveUsers
    };
  }, [userStats]);

  // Calculate station statistics
  const stationStats = useMemo(() => {
    if (!stations) return { total: 0, active: 0, inactive: 0, idle: 0, paused: 0 };
    return {
      total: stations.length,
      active: stations.filter(s => s.status === 'active').length,
      inactive: stations.filter(s => s.status === 'inactive').length,
      idle: stations.filter(s => s.status === 'idle').length,
      paused: stations.filter(s => s.status === 'paused').length,
    };
  }, [stations]);

  const totalProducts = products?.pagination?.total || 0;
  const totalCapacity = capacity?.reduce((sum, c) => sum + c.total_quantity, 0) || 0;
  const lowStockCount = capacity?.reduce((sum, c) => sum + c.low_stock_items, 0) || 0;

  const isLoading = loadingUsers || loadingLocations || loadingStations || loadingProducts || loadingCapacity;

  return (
    <div className="space-y-6">
      {/* Last Updated */}
      <div className="flex justify-end">
        <div className="flex items-center space-x-2 text-sm text-secondary-500">
          <Clock className="w-4 h-4" />
          <span>Last updated: {new Date().toLocaleTimeString()}</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={calculatedUserStats.total}
          icon={Users}
          color="bg-blue-600"
          subtitle={`${calculatedUserStats.active} active users`}
          onClick={() => navigate('/users')}
        />
        <StatCard
          title="Locations"
          value={locationStats?.total || 0}
          icon={MapPin}
          color="bg-green-600"
          subtitle={`${locationStats?.active || 0} active locations`}
          onClick={() => navigate('/locations')}
        />
        <StatCard
          title="Stations"
          value={stationStats.total}
          icon={Building2}
          color="bg-purple-600"
          subtitle={`${stationStats.active} active stations`}
          onClick={() => navigate('/stations')}
        />
        <StatCard
          title="Products"
          value={totalProducts.toLocaleString()}
          icon={Package}
          color="bg-orange-600"
          subtitle={`${totalCapacity.toLocaleString()} total units`}
          onClick={() => navigate('/inventory/products')}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* System Status */}
        <div className="lg:col-span-2 card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Activity className="w-5 h-5 text-secondary-600 mr-2" />
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">System Status</h3>
            </div>
            <span className="text-xs text-secondary-500">Real-time monitoring</span>
          </div>
          
          <div className="space-y-3">
            <div className="bg-secondary-50 dark:bg-gray-700 rounded-lg p-4">
              <h4 className="text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-3">User Accounts</h4>
              <SystemStatusItem
                label="Active Users"
                value={calculatedUserStats.active}
                total={calculatedUserStats.total}
                status="success"
              />
              <SystemStatusItem
                label="Inactive Users"
                value={calculatedUserStats.inactive}
                total={calculatedUserStats.total}
                status="warning"
              />
            </div>

            <div className="bg-secondary-50 dark:bg-gray-700 rounded-lg p-4">
              <h4 className="text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-3">Stations</h4>
              <SystemStatusItem
                label="Active Stations"
                value={stationStats.active}
                total={stationStats.total}
                status="success"
              />
              <SystemStatusItem
                label="Inactive Stations"
                value={stationStats.inactive}
                total={stationStats.total}
                status="warning"
              />
              <SystemStatusItem
                label="Inactive/Idle Stations"
                value={stationStats.inactive}
                total={stationStats.total}
                status="warning"
              />
            </div>

            <div className="bg-secondary-50 dark:bg-gray-700 rounded-lg p-4">
              <h4 className="text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-3">Locations</h4>
              <SystemStatusItem
                label="Active Locations"
                value={locationStats?.active || 0}
                total={locationStats?.total || 0}
                status="success"
              />
              <SystemStatusItem
                label="Inactive Locations"
                value={locationStats?.inactive || 0}
                total={locationStats?.total || 0}
                status="warning"
              />
            </div>
          </div>
        </div>

        {/* Alerts & Quick Actions */}
        <div className="space-y-6">
          {/* Alerts */}
          <div className="card">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-5 h-5 text-orange-600 mr-2" />
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Alerts</h3>
            </div>
            <div className="space-y-3">
              {lowStockCount > 0 && (
                <div className="flex items-start space-x-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-orange-900">Low Stock Alert</p>
                    <p className="text-xs text-orange-700 mt-1">
                      {lowStockCount} {lowStockCount === 1 ? 'item' : 'items'} below reorder level
                    </p>
                  </div>
                </div>
              )}
              
              {stationStats.inactive > 0 && (
                <div className="flex items-start space-x-3 p-3 bg-red-50 rounded-lg border border-red-200">
                  <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-900">Inactive/Idle Stations</p>
                    <p className="text-xs text-red-700 mt-1">
                      {stationStats.inactive} {stationStats.inactive === 1 ? 'station' : 'stations'} not active
                    </p>
                  </div>
                </div>
              )}

              {calculatedUserStats.inactive > 0 && (
                <div className="flex items-start space-x-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-900">Inactive Users</p>
                    <p className="text-xs text-yellow-700 mt-1">
                      {calculatedUserStats.inactive} {calculatedUserStats.inactive === 1 ? 'user account' : 'user accounts'} disabled
                    </p>
                  </div>
                </div>
              )}

              {!lowStockCount && stationStats.inactive === 0 && calculatedUserStats.inactive === 0 && (
                <div className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-900">All Systems Operational</p>
                    <p className="text-xs text-green-700 mt-1">No alerts at this time</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card">
            <div className="flex items-center mb-4">
              <Boxes className="w-5 h-5 text-secondary-600 mr-2" />
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Quick Actions</h3>
            </div>
            <div className="space-y-3">
              <QuickAction
                title="Add New User"
                description="Create a new user account"
                icon={Users}
                color="bg-blue-600"
                onClick={() => navigate('/users')}
              />
              <QuickAction
                title="Add Station"
                description="Setup a new production station"
                icon={Building2}
                color="bg-purple-600"
                onClick={() => navigate('/stations')}
              />
              <QuickAction
                title="Add Location"
                description="Create a new location"
                icon={MapPin}
                color="bg-green-600"
                onClick={() => navigate('/locations')}
              />
              <QuickAction
                title="View Inventory"
                description="Check product catalog"
                icon={Package}
                color="bg-orange-600"
                onClick={() => navigate('/inventory/products')}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity & Platform Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Activity className="w-5 h-5 text-secondary-600 mr-2" />
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Recent Activity</h3>
            </div>
            <span className="text-xs text-secondary-500">Last 24 hours</span>
          </div>
          <div className="space-y-3">
            {recentActivity && recentActivity.length > 0 ? (
              recentActivity.map((activity, index) => (
                <div key={index} className="flex items-start space-x-3 py-2 border-b border-secondary-100 last:border-0">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-secondary-900 dark:text-white font-medium truncate">{activity.description}</p>
                    <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">
                      {activity.full_name || activity.username} • {formatDateTime(activity.timestamp)}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-secondary-500">
                <Activity className="w-12 h-12 mx-auto mb-2 opacity-20" />
                <p className="text-sm">No recent activity</p>
              </div>
            )}
          </div>
        </div>

        {/* Platform Summary */}
        <div className="card">
          <div className="flex items-center mb-4">
            <Boxes className="w-5 h-5 text-secondary-600 mr-2" />
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Platform Summary</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{calculatedUserStats.total}</div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">Total Users</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{locationStats?.total || 0}</div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">Total Locations</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{stationStats.total}</div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">Total Stations</div>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">{totalProducts.toLocaleString()}</div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">Products in Catalog</div>
            </div>
          </div>
        </div>
      </div>

      {isLoading && !userStats && !locationStats && !stations && (
        <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-xl">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
              <span className="text-secondary-700">Loading dashboard data...</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;